import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useGetMergedCreativeMetricsQuery } from "../../store/apis/creativeMetricsApi";
import { components } from "../../types/api";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { exportCreativeMetricsToCSV } from "../../utils/exportCreativeMetricsUtils";
import { getCurrencySymbol } from "../../utils/currencyUtils";
import {
  formatCurrency,
  formatNumber,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";

type Creative = components["schemas"]["CreativeMetricsSummary"];
type Order = "asc" | "desc";

/**
 * Columnas ordenables, en el orden en que se pintan.
 *
 * La primera —la pieza, con miniatura, titular y descripcion— no esta aqui:
 * ordena por `creativeName` pero se renderiza aparte porque no es una celda de
 * un solo valor.
 */
const COLUMNS: {
  field: keyof Creative;
  labelKey: string;
  fallback: string;
  align?: "center";
}[] = [
  {
    field: "creativeName",
    labelKey: "campaigns.creativeName",
    fallback: "Creative Name",
  },
  {
    field: "enabled",
    labelKey: "campaigns.enabled",
    fallback: "Enabled",
    align: "center",
  },
  {
    field: "impressions",
    labelKey: "campaigns.impressions",
    fallback: "Impressions",
  },
  { field: "clicks", labelKey: "campaigns.clicks", fallback: "Clicks" },
  { field: "ctr", labelKey: "campaigns.ctr", fallback: "CTR" },
  { field: "cpm", labelKey: "campaigns.cpm", fallback: "CPM" },
  { field: "cpc", labelKey: "campaigns.cpc", fallback: "CPC" },
  { field: "cost", labelKey: "campaigns.cost", fallback: "Cost" },
];

export interface CreativeMetricsSectionProps {
  /** Merge cuyas subcampanas agregan el desglose. */
  mergedCampaignId?: string;
  /** Nombre usado como base del fichero exportado. */
  campaignName?: string;
  currency?: string;
  /** Ventana de la campana, solo para la etiqueta de la cabecera. */
  startDate?: string;
  endDate?: string;
  /** Título de la sección; por defecto el genérico de la tabla. */
  title?: string;
}

/**
 * Desglose por creatividad de una campana agrupada.
 *
 * Igual que el de sites, estaba escrito en linea dentro del dashboard de
 * NatAds. DV360 escribe en la misma tabla `CreativeMetrics` y el endpoint
 * `/creative-metrics/merged/{id}` no filtra por plataforma, asi que sirve para
 * los dos dashboards.
 *
 * ## Sobre las cifras
 *
 * Es una foto de totales de vida de la campana, no una serie diaria: la
 * ingesta borra y reescribe. Por eso no acepta filtro de fechas — ver el
 * mismo razonamiento en [[PublisherMetricsSection]].
 *
 * El interruptor de "activa" es de solo lectura: refleja el estado que
 * devolvio la plataforma en el ultimo sync, no es un control.
 */
const CreativeMetricsSection: React.FC<CreativeMetricsSectionProps> = ({
  mergedCampaignId,
  campaignName,
  currency,
  startDate,
  endDate,
  title,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<string>("impressions");

  const { data, error, isLoading } = useGetMergedCreativeMetricsQuery(
    { path: { mergedCampaignId: mergedCampaignId || "" } },
    {
      skip: !mergedCampaignId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  useEffect(() => {
    if (error) {
      dispatch(
        setToastMessage({
          message: t("campaigns.creativeMetricsError", {
            defaultValue: "Failed to load creative metrics",
          }),
          severity: "error",
        })
      );
    }
  }, [error, dispatch, t]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const creatives = data?.creatives;

  const sorted = useMemo(() => {
    if (!creatives) return [];
    return [...creatives].sort((a: Creative, b: Creative) => {
      const aVal = a[orderBy as keyof Creative] ?? 0;
      const bVal = b[orderBy as keyof Creative] ?? 0;
      if (bVal < aVal) return order === "asc" ? 1 : -1;
      if (bVal > aVal) return order === "asc" ? -1 : 1;
      return 0;
    });
  }, [creatives, order, orderBy]);

  const handleExport = () => {
    if (!creatives || creatives.length === 0) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    exportCreativeMetricsToCSV(
      creatives,
      campaignName || "export",
      t,
      dispatch,
      getCurrencySymbol(currency || "EUR")
    );
  };

  if (!data) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title ??
              t("campaigns.creativeMetrics", {
                defaultValue: "Creative Metrics",
              })}
          </Typography>
          {startDate && endDate && (
            <Box
              sx={{
                display: "flex",
                bgcolor: "primary.light",
                color: "primary.contrastText",
                borderRadius: 1,
                px: 1,
                py: 0.5,
                fontSize: "0.75rem",
                fontWeight: 500,
                alignItems: "center",
              }}
            >
              {`${startDate} - ${endDate}`}
            </Box>
          )}
        </Box>

        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          sx={{
            height: 36,
            textTransform: "none",
            fontWeight: 500,
            alignSelf: { xs: "flex-start", sm: "auto" },
          }}
          disabled={!creatives || creatives.length === 0}
        >
          {t("campaigns.exportCreativeMetrics", {
            defaultValue: "Export Data",
          })}
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : sorted.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, maxHeight: 600, overflow: "auto" }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-label="creative metrics table"
          >
            <TableHead sx={{ backgroundColor: theme.palette.grey[200] }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={orderBy === "creativeName"}
                    direction={orderBy === "creativeName" ? order : "asc"}
                    onClick={() => handleRequestSort("creativeName")}
                  >
                    {t("campaigns.creative", { defaultValue: "Creative" })}
                  </TableSortLabel>
                </TableCell>
                {COLUMNS.map((column) => (
                  <TableCell
                    key={column.field}
                    sx={{
                      fontWeight: "bold",
                      ...(column.align ? { textAlign: column.align } : {}),
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : "asc"}
                      onClick={() => handleRequestSort(column.field)}
                    >
                      {t(column.labelKey, { defaultValue: column.fallback })}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((creative: Creative) => (
                <TableRow
                  key={creative.creativeId}
                  hover
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    transition: "background-color 0.3s ease",
                    "&:hover": { backgroundColor: theme.palette.grey[100] },
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {creative.thumbnailUrl && (
                        <Box
                          component="img"
                          src={creative.thumbnailUrl}
                          alt={creative.creativeName}
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: 1,
                          }}
                          onError={(e) => {
                            // La miniatura la sirve la plataforma y caduca; si
                            // no carga se oculta en vez de dejar el icono roto.
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                      <Box>
                        <Typography variant="subtitle2">
                          {creative.headline}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {creative.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {creative.creativeName ? (
                      <Typography variant="body2" color="text.secondary">
                        {creative.creativeName}
                      </Typography>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Switch
                      checked={creative.enabled === true}
                      disabled={true}
                      size="small"
                      color={creative.enabled === true ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>{formatNumber(creative.impressions)}</TableCell>
                  <TableCell>{formatNumber(creative.clicks)}</TableCell>
                  <TableCell>
                    {formatPercentageNoMultiply(creative.ctr)}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(creative.cpm, currency)}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(creative.cpc, currency)}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(creative.cost, currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {t("campaigns.noCreativeMetrics", {
              defaultValue: "No creative metrics available",
            })}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CreativeMetricsSection;
